"use client"

import { buttonVariants } from "@/app/ui/forms/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/app/ui/imported/alert-dialog";
import Spinner from "@/app/ui/utils/Spinner";
import { cn } from "@/lib/utils";
import { ComponentProps, ReactNode, useEffect, useState } from "react";

interface CustomDialogProps extends ComponentProps<typeof AlertDialog> {
    /** component that on click will trigger the dialog */
    button?: ReactNode,
    /** the title shown at the top of the dialog */
    title?: ReactNode,
    /** any inner content */
    children: ReactNode,
    /** the action btn can be string / reactNode */
    actionBtn?: ReactNode,
    /** hints user to cancel the dialog */
    assumeCancel?: boolean,
    /** an function that will be called once user clicks the "ok" btn return true for BAD action */
    onOk?: () => Promise<boolean>,
    /** used when no buttons should be render - and close mechanics should relay on the {open} props */
    noBtns?: boolean,
    className?: string,
}

export default function CustomDialog({ button, title, children, actionBtn = "אישור", noBtns, assumeCancel = false, onOk, className, ...props }: CustomDialogProps) {
    const [open, setOpen] = useState(props.open);
    const [isLoading, setIsLoading] = useState(false);

    if (noBtns && props.open == undefined)
        throw new Error("a dialog component was used without buttons / open state-management - essentially there is no way to close this Dialog!")

    useEffect(() => {
        setOpen(props.open);
    }, [props.open])

    return <AlertDialog { ...props } onOpenChange={ (isOpen) => {
        props.onOpenChange && props.onOpenChange(isOpen);
        setOpen(isOpen)
    } } open={ open }>
        { button && <AlertDialogTrigger asChild>{ button }</AlertDialogTrigger> }
        <AlertDialogContent dir="rtl" aria-describedby="dialog-description" className={ className }>
            <AlertDialogHeader>
                <AlertDialogTitle className="flex gap-2 items-center" asChild={ typeof title != "string" }>
                    { title }
                </AlertDialogTitle>
                <AlertDialogDescription id="dialog-description" className="flex flex-col gap-2" asChild={ typeof children != "string" }>
                    { children }
                </AlertDialogDescription>
            </AlertDialogHeader>
            { !noBtns &&
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={ isLoading } autoFocus={ assumeCancel }>ביטול</AlertDialogCancel>
                    <AlertDialogAction onClick={ async (e) => {
                        //if not CB action close modal
                        if (!onOk && !isLoading)
                            return;

                        //prevent modal close
                        e.preventDefault();
                        if (isLoading || !onOk) //if loading pre action - doest resend action
                            return

                        //set loading
                        setIsLoading(true);
                        const isBadAction = await onOk(); //run action
                        setOpen(isBadAction);
                        //reset state
                        setIsLoading(false);
                    } } disabled={ isLoading } autoFocus={ !assumeCancel } className={ cn(typeof actionBtn == "string" && buttonVariants()) }
                        asChild={ typeof actionBtn != "string" }>{
                            isLoading ? <div className="text-3xl mx-3"><Spinner className="text-nfBlue-400" /></div> : actionBtn
                        }</AlertDialogAction>
                </AlertDialogFooter>
            }

        </AlertDialogContent>
    </AlertDialog>
}